import { put, select, delay } from 'redux-saga/effects'

import { LAUNCHER_TAG } from 'common/tags'
import Loader from 'common/modules/loader'
import Zoid from 'common/modules/zoid'
import Lifecycle from 'common/modules/lifecycle'

import { creators } from '../actions'
import selectors from '../selectors'

export function* handleDecorators() {
    const { initializeDecorators, adBlockDetector } = yield select(Zoid.selectors.getXprops)
    const adBlocked = yield adBlockDetector()

    const decoratorPayload = yield select(selectors.decoratorPayload, adBlocked)

    initializeDecorators(decoratorPayload)
}

export function* handleMutators(dispatch, getState) {
    const { sendHostMessage } = yield select(Zoid.selectors.getXprops)

    const mutators = [
        {
            message: 'REGISTER/MENU',
            onMessage: (link) => {
                dispatch(creators.handleDirectLinks({ link }))
            },
        },
        {
            message: 'REGISTER/PROMPT',
            onMessage: (link) => {
                dispatch(creators.handleDirectLinks({ link }))
            },
        },
        {
            message: 'REGISTER/FORM',
            onMessage: (email) => {
                dispatch(creators.handleSignupConfirmation({ email }))
            },
        },
        {
            message: 'REGISTER/PLAN_OPTION',
            onMessage: (planOptionId) => {
                dispatch(creators.handleDirectPlanLink({ planOptionId }))
            },
        },
        {
            message: 'REGISTER/CLOSE_PICO',
            onMessage: () => {
                dispatch(creators.closePico())
            },
        },
        {
            message: 'REGISTER/GET_LIFECYCLE_EVENTS',
            onMessage: async () => {
                const state = getState()
                const lifecycleEvents = Lifecycle.selectors.getEvents(state)

                return lifecycleEvents
            },
        },
    ]

    let mutatorsHandled = 0

    const onMutatorHandled = () => {
        mutatorsHandled += 1

        if (mutatorsHandled === mutators.length) {
            dispatch(Lifecycle.creators.mutatorsReady())
        }
    }

    mutators.forEach(({
        message,
        onMessage,
    }) => {
        sendHostMessage(
            message,
            onMessage,
            onMutatorHandled,
        )
    })
}

export function* handlePrompt() {
    const { hideArticle, queryString = {} } = yield select(Zoid.selectors.getXprops)

    const loader = yield select(Loader.selectors.loader)
    const scope = (loader.user) ? 'user' : 'guest'
    const scopedLoader = loader[scope]

    // eslint-disable-next-line camelcase
    const { prompt, publisher, userArticle } = scopedLoader || {}

    const canHandlePrompt = !!prompt
    && !queryString.pn
    && (
        !queryString.pico_ui
        || queryString.pico_ui === 'login_link'
        || queryString.pico_ui === 'verified'
    )

    if (!canHandlePrompt) {
        if (!userArticle) {
            yield put(creators.purchaseArticle())
        }

        return null
    }

    const linkColor = yield select(Loader.selectors.linkColor)

    const promptType = (prompt.type || '').toLowerCase()

    if (prompt.is_required) {
        try {
            yield hideArticle({
                prompt: promptType,
                style: linkColor,
                // eslint-disable-next-line camelcase
                text: publisher.article_lock_button_text,
            })
        } catch (error) {
            // eslint-disable-next-line no-console
            console.log(error)
        }

        yield put(creators.showChild({ frame: LAUNCHER_TAG }))
    } else {
        if (!userArticle) {
            yield put(creators.purchaseArticle())
        }

        if (prompt.delay) {
            yield delay(prompt.delay)
        }

        return {
            promptType,
        }
    }

    return null
}

export default function* watchLoaderActions({ dispatch, getState }) {
    const { queryString = {} } = yield select(Zoid.selectors.getXprops)
    const loader = yield select(Loader.selectors.loader)
    const ruleTriggerId = yield select(Loader.selectors.promptTriggerRuleId)
    const planTriggerId = yield select(Loader.selectors.promptTriggerPlanId)

    const scopedLoader = loader.user || loader.guest || null

    yield handleDecorators()
    yield handleMutators(dispatch, getState)
    const promptData = yield handlePrompt()

    if (queryString.pn) {
        yield put(creators.handleDirectLinks({ link: queryString.pn, queryString }))
        yield put(Lifecycle.creators.directLinkPrompt({
            payload: {
                planTriggerId,
            },
        }))
    } else if (queryString.pico_ui) {
        yield put(creators.handleDirectLinks({ link: queryString.pico_ui, queryString }))
    } else if (scopedLoader?.prompt) {
        const promptType = promptData?.promptType

        yield put(creators.handleDirectLinks({
            link: promptType === 'login' ? 'registration' : promptType,
        }))
        yield put(Lifecycle.creators.popupPrompt({
            payload: {
                promptType,
                triggerId: promptType === 'login' ? ruleTriggerId : planTriggerId,
            },
        }))
    } else {
        yield put(creators.showChild({ frame: LAUNCHER_TAG }))

        if (scopedLoader && !scopedLoader?.userArticle) {
            yield put(creators.purchaseArticle())
        }
    }
}
